import type { Contractor } from "@/data/types";

export const WEBSITE_PARENT_SUBCONTRACTOR_REVIEW_SCOPE_REQUIRED =
  "WEBSITE_PARENT_SUBCONTRACTOR_REVIEW_SCOPE_REQUIRED";
export const CANONICAL_SUBCONTRACTOR_IDENTITY_REQUIRED =
  "CANONICAL_SUBCONTRACTOR_IDENTITY_REQUIRED";
export const CANONICAL_SUBCONTRACTOR_RELATIONSHIP_AMBIGUOUS =
  "CANONICAL_SUBCONTRACTOR_RELATIONSHIP_AMBIGUOUS";

export const WEBSITE_PARENT_SUBCONTRACTOR_REVIEW_SCOPE_MESSAGE =
  "Canonical Individual Compliance cannot yet be reviewed for this relationship because the Website reviewer contract does not currently recognize the parent-to-subcontractor relationship as reviewer scope.";
export const CANONICAL_SUBCONTRACTOR_IDENTITY_MESSAGE =
  "This subcontractor is linked to the parent, but no authenticated individual identity is available for canonical Individual Compliance review.";
export const CANONICAL_SUBCONTRACTOR_RELATIONSHIP_AMBIGUOUS_MESSAGE =
  "This subcontractor relationship is ambiguous. Canonical review is unavailable until the parent, organization, and person relationship are unambiguous.";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type CanonicalOnboardingRosterPerson = Readonly<{
  contractorId: string;
  userId: string | null;
  role: string | null;
  status: string | null;
  label: string;
  email?: string | null;
}>;

export type CanonicalOnboardingRelatedPerson = Readonly<{
  contractorId: string;
  userId: string | null;
  label: string;
  relationshipLabel: string;
  roleLabel?: string | null;
}>;

export type CanonicalOnboardingReviewSubject = Readonly<{
  contractorId: string;
  targetPersonUserId: string;
  label: string;
  roleLabel: string;
  relationshipLabel: string;
}>;

export type CanonicalOnboardingRelatedPersonState = Readonly<{
  userId: string | null;
  displayName: string;
  roleLabel: string | null;
  parentRosterActive: boolean;
  childRosterActive: boolean;
  canReviewCanonically: boolean;
  blockerCode:
    | typeof WEBSITE_PARENT_SUBCONTRACTOR_REVIEW_SCOPE_REQUIRED
    | typeof CANONICAL_SUBCONTRACTOR_IDENTITY_REQUIRED
    | typeof CANONICAL_SUBCONTRACTOR_RELATIONSHIP_AMBIGUOUS
    | null;
  blockerMessage: string | null;
}>;

export type CanonicalOnboardingRelationshipReviewability =
  | "reviewable"
  | "blocked_scope"
  | "informational_only"
  | "ambiguous_relationship";

export type CanonicalOnboardingRelatedContractor = Readonly<{
  contractorId: string;
  contractorName: string;
  contractorType: string;
  parentContractorId: string;
  parentContractorName: string;
  depth: number;
  relationshipLabel: string;
  linkedPeople: readonly CanonicalOnboardingRelatedPersonState[];
  reviewability: CanonicalOnboardingRelationshipReviewability;
  blockerCode:
    | typeof WEBSITE_PARENT_SUBCONTRACTOR_REVIEW_SCOPE_REQUIRED
    | typeof CANONICAL_SUBCONTRACTOR_IDENTITY_REQUIRED
    | typeof CANONICAL_SUBCONTRACTOR_RELATIONSHIP_AMBIGUOUS
    | null;
  blockerMessage: string | null;
}>;

export type CanonicalOnboardingBlockedRelationship = Readonly<{
  contractorId: string;
  label: string;
  relationshipLabel: string;
  targetPersonUserId: string | null;
  blocker: typeof WEBSITE_PARENT_SUBCONTRACTOR_REVIEW_SCOPE_REQUIRED;
}>;

export type CanonicalOnboardingReviewContext = Readonly<{
  organizationId: string;
  organizationLabel: string;
  parentContractorId: string;
  parentContractorName: string;
  parentPersonnel: readonly CanonicalOnboardingReviewSubject[];
  relatedContractors: readonly CanonicalOnboardingRelatedContractor[];
  subcontractors: readonly CanonicalOnboardingReviewSubject[];
  crew: readonly CanonicalOnboardingReviewSubject[];
  blockedRelationships: readonly CanonicalOnboardingBlockedRelationship[];
}>;

function usableUuid(value: string | null | undefined): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function normalize(value: string | null | undefined): string {
  return String(value ?? "").trim();
}

function hasConflictingContractorRows(
  first: Contractor,
  second: Contractor,
): boolean {
  return first.parentEntityId !== second.parentEntityId
    || first.organizationId !== second.organizationId
    || first.contractorType !== second.contractorType;
}

function uniqueContractors(
  contractors: readonly Contractor[],
): Map<string, Contractor> | null {
  const byId = new Map<string, Contractor>();
  for (const contractor of contractors) {
    const existing = byId.get(contractor.id);
    if (existing && hasConflictingContractorRows(existing, contractor)) {
      return null;
    }
    if (!existing) byId.set(contractor.id, contractor);
  }
  return byId;
}

export function resolveCanonicalOnboardingParent(
  selectedContractorId: string | null,
  contractors: readonly Contractor[],
): Contractor | null {
  if (!usableUuid(selectedContractorId)) return null;
  const byId = uniqueContractors(contractors);
  if (!byId) return null;
  let current = byId.get(selectedContractorId) ?? null;
  const visited = new Set<string>();
  while (current) {
    if (visited.has(current.id)) return null;
    visited.add(current.id);
    if (!current.parentEntityId) {
      return current.contractorType === "sub_parent" ? null : current;
    }
    if (!usableUuid(current.parentEntityId)) return null;
    current = byId.get(current.parentEntityId) ?? null;
    if (!current) return null;
  }
  return null;
}

function subjectFromRoster(
  person: CanonicalOnboardingRosterPerson,
  relationshipLabel: string,
): CanonicalOnboardingReviewSubject {
  return Object.freeze({
    contractorId: person.contractorId,
    targetPersonUserId: person.userId as string,
    label: person.label,
    roleLabel: normalize(person.role) || "crew",
    relationshipLabel,
  });
}

type StructuralContractor = Readonly<{
  contractor: Contractor;
  parent: Contractor;
  depth: number;
  ambiguous: boolean;
}>;

function collectStructuralDescendants(
  parent: Contractor,
  contractors: readonly Contractor[],
  organizationId: string,
): readonly StructuralContractor[] {
  const byId = uniqueContractors(contractors);
  if (!byId) return [];
  const childrenByParent = new Map<string, Contractor[]>();
  for (const contractor of byId.values()) {
    if (
      contractor.id === parent.id
      || !usableUuid(contractor.parentEntityId)
    ) {
      continue;
    }
    const children = childrenByParent.get(contractor.parentEntityId) ?? [];
    children.push(contractor);
    childrenByParent.set(contractor.parentEntityId, children);
  }

  const result: StructuralContractor[] = [];
  const visited = new Set<string>([parent.id]);
  const queue = (childrenByParent.get(parent.id) ?? []).map((contractor) => ({
    contractor,
    parent,
    depth: 1,
  }));
  while (queue.length > 0) {
    const next = queue.shift();
    if (!next) break;
    const { contractor, depth } = next;
    if (visited.has(contractor.id)) continue;
    visited.add(contractor.id);
    const parentContractor = byId.get(contractor.parentEntityId ?? "");
    if (!parentContractor) continue;
    const ambiguous =
      contractor.organizationId !== organizationId
      || contractor.contractorType === "main_parent";
    result.push(Object.freeze({
      contractor,
      parent: parentContractor,
      depth,
      ambiguous,
    }));
    for (const child of childrenByParent.get(contractor.id) ?? []) {
      if (!visited.has(child.id)) {
        queue.push({
          contractor: child,
          parent: contractor,
          depth: depth + 1,
        });
      }
    }
  }
  return result;
}

function relationshipLabel(
  contractor: Contractor,
): string {
  if (contractor.contractorType === "sub_parent") {
    return "Sub-parent contractor";
  }
  return normalize(contractor.relationshipType) || "Related contractor";
}

function linkedPeopleForContractor(input: Readonly<{
  contractor: Contractor;
  ambiguous: boolean;
  parentRosterByUserId: ReadonlyMap<string, CanonicalOnboardingRosterPerson>;
  roster: readonly CanonicalOnboardingRosterPerson[];
  relatedPeople: readonly CanonicalOnboardingRelatedPerson[];
}>): readonly CanonicalOnboardingRelatedPersonState[] {
  const childRoster = input.roster.filter(
    (person) => person.contractorId === input.contractor.id,
  );
  const childRosterByUserId = new Map(
    childRoster
      .filter((person) => usableUuid(person.userId))
      .map((person) => [person.userId as string, person]),
  );
  const candidates: Array<{
    userId: string | null;
    displayName: string;
    roleLabel: string | null;
  }> = input.relatedPeople
    .filter((person) => person.contractorId === input.contractor.id)
    .map((person) => ({
      userId: usableUuid(person.userId) ? person.userId : null,
      displayName: normalize(person.label) || input.contractor.companyName,
      roleLabel: normalize(person.roleLabel) || null,
    }));
  for (const person of childRoster) {
    const userId = usableUuid(person.userId) ? person.userId : null;
    if (
      userId
      && candidates.some((candidate) => candidate.userId === userId)
    ) {
      continue;
    }
    candidates.push({
      userId,
      displayName: normalize(person.label) || input.contractor.companyName,
      roleLabel: normalize(person.role) || null,
    });
  }
  if (candidates.length === 0) {
    candidates.push({
      userId: null,
      displayName: input.contractor.companyName,
      roleLabel: null,
    });
  }

  return Object.freeze(candidates.map((candidate) => {
    const parentRoster = candidate.userId
      ? input.parentRosterByUserId.get(candidate.userId)
      : null;
    const childMembership = candidate.userId
      ? childRosterByUserId.get(candidate.userId)
      : null;
    const parentRosterActive = parentRoster?.status === "active";
    const childRosterActive = childMembership?.status === "active";
    const canReviewCanonically =
      !input.ambiguous
      && Boolean(candidate.userId)
      && (parentRosterActive || childRosterActive);
    const blockerCode = input.ambiguous
      ? CANONICAL_SUBCONTRACTOR_RELATIONSHIP_AMBIGUOUS
      : !candidate.userId
        ? CANONICAL_SUBCONTRACTOR_IDENTITY_REQUIRED
        : null;
    const blockerMessage =
      blockerCode === CANONICAL_SUBCONTRACTOR_RELATIONSHIP_AMBIGUOUS
        ? CANONICAL_SUBCONTRACTOR_RELATIONSHIP_AMBIGUOUS_MESSAGE
        : blockerCode === CANONICAL_SUBCONTRACTOR_IDENTITY_REQUIRED
          ? CANONICAL_SUBCONTRACTOR_IDENTITY_MESSAGE
          : null;
    return Object.freeze({
      userId: candidate.userId,
      displayName: candidate.displayName,
      roleLabel:
        candidate.roleLabel
        || normalize(childMembership?.role)
        || normalize(parentRoster?.role)
        || null,
      parentRosterActive,
      childRosterActive,
      canReviewCanonically,
      blockerCode,
      blockerMessage,
    });
  }));
}

export function buildCanonicalOnboardingReviewContext(input: Readonly<{
  organizationId?: string | null;
  selectedContractorId: string | null;
  contractors: readonly Contractor[];
  roster: readonly CanonicalOnboardingRosterPerson[];
  relatedPeople?: readonly CanonicalOnboardingRelatedPerson[];
}>): CanonicalOnboardingReviewContext | null {
  const parent = resolveCanonicalOnboardingParent(
    input.selectedContractorId,
    input.contractors,
  );
  const organizationId =
    usableUuid(input.organizationId)
      ? input.organizationId
      : usableUuid(parent?.organizationId)
        ? parent.organizationId
        : null;

  if (
    !parent
    || !usableUuid(parent.id)
    || !organizationId
    || (
      parent.organizationId
      && parent.organizationId !== organizationId
    )
  ) {
    return null;
  }

  const activeParentRoster = input.roster.filter((person) =>
    person.contractorId === parent.id
    && person.status === "active"
    && usableUuid(person.userId)
  );

  const parentRosterByUserId = new Map(
    activeParentRoster.map((person) => [person.userId as string, person]),
  );

  const parentPersonnel: CanonicalOnboardingReviewSubject[] = [];
  const crew: CanonicalOnboardingReviewSubject[] = [];
  for (const person of activeParentRoster) {
    const subject = subjectFromRoster(person, "Parent contractor");
    if (person.role === "owner" || person.role === "lead") {
      parentPersonnel.push(subject);
    } else {
      crew.push(subject);
    }
  }

  const structuralDescendants = collectStructuralDescendants(
    parent,
    input.contractors,
    organizationId,
  );
  const relatedPeople = input.relatedPeople ?? [];
  const relatedContractors: CanonicalOnboardingRelatedContractor[] = [];
  const subcontractors: CanonicalOnboardingReviewSubject[] = [];
  const blockedRelationships: CanonicalOnboardingBlockedRelationship[] = [];

  for (const structural of structuralDescendants) {
    const linkedPeople = linkedPeopleForContractor({
      contractor: structural.contractor,
      ambiguous: structural.ambiguous,
      parentRosterByUserId,
      roster: input.roster,
      relatedPeople,
    });
    const reviewability: CanonicalOnboardingRelationshipReviewability =
      structural.ambiguous
        ? "ambiguous_relationship"
        : linkedPeople.some((person) => person.canReviewCanonically)
          ? "reviewable"
          : "informational_only";
    const blockerCode =
      reviewability === "ambiguous_relationship"
        ? CANONICAL_SUBCONTRACTOR_RELATIONSHIP_AMBIGUOUS
        : reviewability === "informational_only"
            ? CANONICAL_SUBCONTRACTOR_IDENTITY_REQUIRED
            : null;
    const blockerMessage =
      blockerCode === CANONICAL_SUBCONTRACTOR_RELATIONSHIP_AMBIGUOUS
        ? CANONICAL_SUBCONTRACTOR_RELATIONSHIP_AMBIGUOUS_MESSAGE
        : blockerCode === CANONICAL_SUBCONTRACTOR_IDENTITY_REQUIRED
            ? CANONICAL_SUBCONTRACTOR_IDENTITY_MESSAGE
            : null;

    for (const person of linkedPeople) {
      if (person.canReviewCanonically && person.userId) {
        subcontractors.push(Object.freeze({
          contractorId: structural.contractor.id,
          targetPersonUserId: person.userId,
          label: person.displayName,
          roleLabel: person.roleLabel ?? "subcontractor",
          relationshipLabel: relationshipLabel(structural.contractor),
        }));
      }
    }
    relatedContractors.push(Object.freeze({
      contractorId: structural.contractor.id,
      contractorName: structural.contractor.companyName,
      contractorType:
        structural.contractor.contractorType
        ?? structural.contractor.entityType
        ?? "related",
      parentContractorId: structural.parent.id,
      parentContractorName: structural.parent.companyName,
      depth: structural.depth,
      relationshipLabel: relationshipLabel(structural.contractor),
      linkedPeople,
      reviewability,
      blockerCode,
      blockerMessage,
    }));
  }

  const byLabel = (
    a: { label: string },
    b: { label: string },
  ) => a.label.localeCompare(b.label);
  return Object.freeze({
    organizationId,
    organizationLabel: `Organization ${organizationId.slice(0, 8)}`,
    parentContractorId: parent.id,
    parentContractorName: parent.companyName,
    parentPersonnel: Object.freeze(parentPersonnel.sort(byLabel)),
    relatedContractors: Object.freeze(relatedContractors),
    subcontractors: Object.freeze(subcontractors.sort(byLabel)),
    crew: Object.freeze(crew.sort(byLabel)),
    blockedRelationships: Object.freeze(
      blockedRelationships.sort(byLabel),
    ),
  });
}
